import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import programJson from '../../programs/program-v1.json'
import type {
  BackupMetadata,
  ExerciseDefinition,
  Measurement,
  Profile,
  Settings,
  TrainingProgram,
  WorkoutExerciseResult,
  WorkoutRecord
} from '../types'

export const DB_NAME = 'personal-trainer'
export const DB_VERSION = 2

interface TrainerDB extends DBSchema {
  profile: { key: string; value: Profile }
  programs: { key: string; value: TrainingProgram }
  exercises: { key: string; value: ExerciseDefinition }
  workouts: { key: string; value: WorkoutRecord; indexes: { 'by-date': string } }
  workoutExercises: { key: string; value: WorkoutExerciseResult; indexes: { 'by-workout': string } }
  measurements: { key: string; value: Measurement; indexes: { 'by-date': string } }
  settings: { key: string; value: Settings }
  backupsMetadata: { key: string; value: BackupMetadata }
}

let dbPromise: Promise<IDBPDatabase<TrainerDB>> | undefined

export function getDb() {
  if (!dbPromise) {
    dbPromise = openDB<TrainerDB>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, _newVersion, transaction) {
        if (oldVersion < 1) {
          db.createObjectStore('profile', { keyPath: 'id' })
          db.createObjectStore('programs', { keyPath: 'id' })
          db.createObjectStore('exercises', { keyPath: 'id' })
          db.createObjectStore('workouts', { keyPath: 'id' })
          db.createObjectStore('workoutExercises', { keyPath: 'id' })
          db.createObjectStore('measurements', { keyPath: 'id' })
          db.createObjectStore('settings', { keyPath: 'id' })
          db.createObjectStore('backupsMetadata', { keyPath: 'id' })
        }
        if (oldVersion < 2) {
          const workouts = transaction.objectStore('workouts')
          const results = transaction.objectStore('workoutExercises')
          const measurements = transaction.objectStore('measurements')
          if (!workouts.indexNames.contains('by-date')) workouts.createIndex('by-date', 'date')
          if (!results.indexNames.contains('by-workout')) results.createIndex('by-workout', 'workoutId')
          if (!measurements.indexNames.contains('by-date')) measurements.createIndex('by-date', 'date')
        }
      },
      blocked() {
        console.warn('Обновление базы ожидает закрытия другой вкладки')
      }
    })
  }
  return dbPromise
}

export async function initializeDatabase() {
  const db = await getDb()
  const program = programJson as TrainingProgram
  if (!(await db.get('profile', 'primary'))) {
    await db.put('profile', {
      id: 'primary',
      heightCm: 184,
      weightKg: 78,
      notes: 'Возвращение к регулярной активности после длительного перерыва.'
    })
  }
  if (!(await db.get('settings', 'settings'))) {
    await db.put('settings', {
      id: 'settings',
      theme: 'system',
      activeProgramId: program.id,
      haptics: true,
      onboardingComplete: true
    })
  }
  const installedProgram = await db.get('programs', program.id)
  const shouldUpgradeBundledProgram = !installedProgram || installedProgram.version < program.version
  if (shouldUpgradeBundledProgram) await db.put('programs', program)
  const tx = db.transaction('exercises', 'readwrite')
  for (const exercise of program.exerciseLibrary) {
    if (shouldUpgradeBundledProgram || !(await tx.store.get(exercise.id))) await tx.store.put(exercise)
  }
  await tx.done
  return db
}

export async function getActiveProgram() {
  const db = await initializeDatabase()
  const settings = await db.get('settings', 'settings')
  const fallback = programJson as TrainingProgram
  return (settings && await db.get('programs', settings.activeProgramId)) || fallback
}

export async function getProfile() {
  const db = await initializeDatabase()
  return db.get('profile', 'primary')
}

export async function saveProfile(profile: Profile) {
  return (await getDb()).put('profile', profile)
}

export async function getSettings() {
  const db = await initializeDatabase()
  return db.get('settings', 'settings')
}

export async function saveSettings(settings: Settings) {
  return (await getDb()).put('settings', settings)
}

export async function saveWorkout(workout: WorkoutRecord, exercises: WorkoutExerciseResult[]) {
  const db = await getDb()
  const tx = db.transaction(['workouts', 'workoutExercises'], 'readwrite')
  await tx.objectStore('workouts').put(workout)
  const existing = await tx.objectStore('workoutExercises').index('by-workout').getAll(workout.id)
  for (const result of existing) await tx.objectStore('workoutExercises').delete(result.id)
  for (const result of exercises) await tx.objectStore('workoutExercises').put(result)
  await tx.done
}

export async function getWorkout(id: string) {
  const db = await getDb()
  const workout = await db.get('workouts', id)
  if (!workout) return undefined
  const exercises = await db.getAllFromIndex('workoutExercises', 'by-workout', id)
  return { workout, exercises }
}

export async function getWorkoutByDate(date: string) {
  const db = await getDb()
  const workout = await db.getFromIndex('workouts', 'by-date', date)
  if (!workout) return undefined
  return getWorkout(workout.id)
}

export async function getHistory() {
  const db = await initializeDatabase()
  const workouts = await db.getAll('workouts')
  return workouts.sort((a, b) => b.date.localeCompare(a.date))
}

export async function getAllWorkoutExercises() {
  return (await getDb()).getAll('workoutExercises')
}

export async function addMeasurement(measurement: Measurement) {
  return (await getDb()).put('measurements', measurement)
}

export async function getMeasurements() {
  const rows = await (await getDb()).getAll('measurements')
  return rows.sort((a, b) => a.date.localeCompare(b.date))
}

export async function installProgram(program: TrainingProgram, activate = false) {
  const db = await getDb()
  const tx = db.transaction(['programs', 'exercises', 'settings'], 'readwrite')
  await tx.objectStore('programs').put(program)
  for (const exercise of program.exerciseLibrary) await tx.objectStore('exercises').put(exercise)
  if (activate) {
    const settings = await tx.objectStore('settings').get('settings')
    if (settings) await tx.objectStore('settings').put({ ...settings, activeProgramId: program.id })
  }
  await tx.done
}

export async function activateProgram(id: string) {
  const db = await getDb()
  const settings = await db.get('settings', 'settings')
  if (!settings) throw new Error('Настройки не найдены')
  await db.put('settings', { ...settings, activeProgramId: id })
}

export async function clearHistory() {
  const db = await getDb()
  const tx = db.transaction(['workouts', 'workoutExercises'], 'readwrite')
  await tx.objectStore('workouts').clear()
  await tx.objectStore('workoutExercises').clear()
  await tx.done
}

export async function requestPersistentStorage(): Promise<boolean | undefined> {
  try {
    if (!navigator.storage?.persist) return undefined
    return await navigator.storage.persist()
  } catch (error) {
    if (import.meta.env.DEV) console.warn('Persistent storage unavailable', error)
    return undefined
  }
}

export function resetDbConnectionForTests() {
  dbPromise = undefined
}
